/**
 * IssueRedirect
 * Handles global /is/:issueId URLs by looking up the issue,
 * resolving its project slug and board, then redirecting to
 * the project-scoped board issue URL.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject, getProjectShortId } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { globalIssueApi } from '../../api/work';

export function IssueRedirect() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const { allProjects, isLoading } = useProject();
  const { wsSlug } = useWorkspace();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading || !issueId) return;

    globalIssueApi.get(issueId)
      .then((issue) => {
        const project = allProjects.find((p) => p.id === issue.project_id);
        if (!project) {
          setError(`Project not found for issue ${issueId}`);
          return;
        }
        const boardId = issue.board_id;
        const shortId = getProjectShortId(project.id);
        const base = wsSlug ? `/w/${wsSlug}/product/${shortId}` : `/product/${shortId}`;
        navigate(`${base}/boards/${boardId}/is/${issueId}`, { replace: true });
      })
      .catch(() => {
        setError(`Issue ${issueId} not found`);
      });
  }, [issueId, allProjects, isLoading, navigate, wsSlug]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-sm text-neutral-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500 mx-auto mb-2"></div>
        <p className="text-sm text-neutral-500">Resolving issue...</p>
      </div>
    </div>
  );
}
